import { action, computed, makeObservable, observable, toJS } from 'mobx';
import Loadable, { lazyLoadable, LoadableCreator } from '@app/utils/Loadable';
import {
  Assignee,
  StatusReason,
  SupplierValidationRecordStatus,
  transformAssignee,
  transformToStatusReason,
  transformValidationSystemOrganization,
  ValidationSystemOrganization,
} from '@mortee/domain/validationSystem';
import validationSystemServices from '@mortee/services/validationSystemServices';
import {
  mergeFieldFilters,
  SupplierValidationRecordFieldFilters,
  SupplierValidationRecordQuickFilter,
  SupplierValidationRecordTableState,
} from '@mortee/domain/validationRecordTable';
import { arrayWithoutValue, getListWithLeading, wrapValueAsArray } from '@app/utils/arrayUtils';
import UserStore from '@app/stores/UserStore';
import { YesNoFilterOption } from '@app/domain/filters';
import { TableView } from '@mortee/domain/validationSystemTableViews';

import { compare } from '@app/utils/comparatorUtils';

type QuickFilterToFieldFiltersConverter = () => SupplierValidationRecordFieldFilters | undefined;

export default class ValidationSystemStore {
  @observable private _allUsers: Loadable<Assignee[]> = LoadableCreator.notStarted();
  @observable private _allAssignableUsers: Loadable<Assignee[]> = LoadableCreator.notStarted();
  @observable private _organizations: Loadable<ValidationSystemOrganization[]> = LoadableCreator.notStarted();
  @observable private _cancelReasons: Loadable<StatusReason[]> = LoadableCreator.notStarted();
  @observable private _clarificationExternalReasons: Loadable<StatusReason[]> = LoadableCreator.notStarted();
  @observable private _topUpRequiredReasons: Loadable<StatusReason[]> = LoadableCreator.notStarted();
  // The records table filter/sort/search/page, persisted here so it survives leaving the table to
  // edit a record and coming back. It can't ride on router location.state: the app's observable
  // history wrapper (mobx-observable-history) re-pushes a bare path — dropping state — whenever a
  // query param (e.g. the table `view`) changes on mount. A store is unaffected by that.
  //
  // `.ref`, and stored as a plain (toJS) snapshot: a deep observable would turn the nested filter
  // arrays into Proxies, which are not structured-cloneable — when that state later flows into a
  // history.push() as router state, pushState throws DataCloneError ("[object Array] could not be
  // cloned"). Keeping it a plain, whole-object snapshot avoids that.
  @observable.ref private _lastTableState: SupplierValidationRecordTableState | undefined = undefined;
  // The table view (Communication/Verification/Approval) the user last had selected when they left the
  // table to edit a record. The view lives only in the `view` URL query param on the table page, which
  // the edit-page route does not carry — persist it here so closing the edit page can restore the same
  // view instead of falling back to the role default.
  @observable private _lastTableView: TableView | undefined = undefined;
  private readonly _userStore: UserStore;
  private readonly _quickFiltersConverters: Record<SupplierValidationRecordQuickFilter, QuickFilterToFieldFiltersConverter>;

  constructor(userStore: UserStore) {
    this._userStore = userStore;
    this._quickFiltersConverters = {
      [SupplierValidationRecordQuickFilter.onlyMine]: this.createOnlyMineFilter.bind(this),
      [SupplierValidationRecordQuickFilter.notCompleted]: this.createNotCompletedFilter.bind(this),
      [SupplierValidationRecordQuickFilter.unlinkedRecords]: this.createUnlinkedRecordsFilter.bind(this),
      [SupplierValidationRecordQuickFilter.manualFollowUp]: this.createManualFollowUpRecordsFilter.bind(this),
    };
    makeObservable(this);
  }

  get lastTableState(): SupplierValidationRecordTableState | undefined {
    return this._lastTableState;
  }

  get lastTableView(): TableView | undefined {
    return this._lastTableView;
  }

  @action
  setLastTableView = (tableView: TableView): void => {
    this._lastTableView = tableView;
  };

  @action
  setLastTableState = (tableState: SupplierValidationRecordTableState): void => {
    // Store a plain snapshot: toJS strips any MobX Proxies from the incoming state (and, with
    // @observable.ref above, nothing re-wraps it), so the persisted arrays stay structured-cloneable
    // for the later history.push() that carries this state.
    this._lastTableState = toJS(tableState);
  };

  @computed
  get allUsers(): Loadable<Assignee[]> {
    return lazyLoadable(
      () => this._allUsers,
      (newValue) => (this._allUsers = newValue),
      async (): Promise<Assignee[]> => {
        const allUsersServerResponse = await validationSystemServices.getAllAssignees();
        const allUsers = allUsersServerResponse.map((user) => transformAssignee(user, this._userStore.user?.id));
        return this.usersWithMeAtStart(allUsers);
      },
    );
  }

  @computed
  get allAssignableUsers(): Loadable<Assignee[]> {
    return lazyLoadable(
      () => this._allAssignableUsers,
      (newValue) => (this._allAssignableUsers = newValue),
      async (): Promise<Assignee[]> => {
        const allAssignableUsersServerResponse = await validationSystemServices.getAllAssignees(true);
        const allAssignableUsers = allAssignableUsersServerResponse.map((user) =>
          transformAssignee(user, this._userStore.user?.id),
        );
        return this.usersWithMeAtStart(allAssignableUsers);
      },
    );
  }

  @computed
  get organizations(): Loadable<ValidationSystemOrganization[]> {
    return lazyLoadable(
      () => this._organizations,
      (newValue) => (this._organizations = newValue),
      async (): Promise<ValidationSystemOrganization[]> => {
        const organizationServerResponses = await validationSystemServices.getAllValidationSystemOrganizations();
        return organizationServerResponses.map(transformValidationSystemOrganization);
      },
    );
  }

  @computed
  get cancelReasons(): Loadable<StatusReason[]> {
    return this.statusReasons(
      SupplierValidationRecordStatus.canceled,
      () => this._cancelReasons,
      (newValue) => (this._cancelReasons = newValue),
    );
  }

  @computed
  get clarificationExternalReasons(): Loadable<StatusReason[]> {
    return this.statusReasons(
      SupplierValidationRecordStatus.clarificationExternal,
      () => this._clarificationExternalReasons,
      (newValue) => (this._clarificationExternalReasons = newValue),
    );
  }

  @computed
  get topUpRequiredReasons(): Loadable<StatusReason[]> {
    return this.statusReasons(
      SupplierValidationRecordStatus.topUpRequired,
      () => this._topUpRequiredReasons,
      (newValue) => (this._topUpRequiredReasons = newValue),
    );
  }

  private statusReasons(
    status: SupplierValidationRecordStatus,
    getter: () => Loadable<StatusReason[]>,
    setter: (newValue: Loadable<StatusReason[]>) => void,
  ): Loadable<StatusReason[]> {
    return lazyLoadable(getter, setter, async (): Promise<StatusReason[]> => {
      const statusReasonServerResponses = await validationSystemServices.getValidationRecordStatusReasons(status);
      return statusReasonServerResponses.map(transformToStatusReason);
    });
  }

  createCombinedFilters = (
    tableFieldFilters: SupplierValidationRecordFieldFilters | undefined,
    quickFilters: SupplierValidationRecordQuickFilter[] | undefined,
  ): SupplierValidationRecordFieldFilters => {
    const quickFiltersBrokenDownToFieldFilters = Object.entries(this._quickFiltersConverters).map(
      ([quickFilterType, quickFilterCreator]: [SupplierValidationRecordQuickFilter, QuickFilterToFieldFiltersConverter]) =>
        this.createFilterFromQuickFilter(quickFilters, quickFilterType, quickFilterCreator),
    );

    return mergeFieldFilters(tableFieldFilters, ...quickFiltersBrokenDownToFieldFilters);
  };

  createFilterFromQuickFilter = (
    quickFilters: SupplierValidationRecordQuickFilter[] | undefined,
    currentQuickFilter: SupplierValidationRecordQuickFilter,
    creator: QuickFilterToFieldFiltersConverter,
  ): SupplierValidationRecordFieldFilters | undefined => {
    if (!quickFilters || !quickFilters.includes(currentQuickFilter)) {
      return undefined;
    }

    return creator();
  };

  createOnlyMineFilter = (): SupplierValidationRecordFieldFilters => {
    return {
      assignee: wrapValueAsArray(this._userStore.user?.id),
      status: undefined,
      organizationId: undefined,
      registrationDate: undefined,
      didUserRegister: undefined,
      automailerStatus: undefined,
      followUpEmailStatus: undefined,
      evidenceType: undefined,
      customerInvolvement: undefined,
      instructionType: undefined,
      followUpFilter: undefined,
      registrationAccountBankCountryCodes: undefined,
    };
  };

  createNotCompletedFilter = (): SupplierValidationRecordFieldFilters => {
    return {
      assignee: undefined,
      status: arrayWithoutValue(Object.values(SupplierValidationRecordStatus), SupplierValidationRecordStatus.completed),
      organizationId: undefined,
      registrationDate: undefined,
      didUserRegister: undefined,
      automailerStatus: undefined,
      followUpEmailStatus: undefined,
      evidenceType: undefined,
      customerInvolvement: undefined,
      instructionType: undefined,
      followUpFilter: undefined,
      registrationAccountBankCountryCodes: undefined,
    };
  };

  createUnlinkedRecordsFilter = (): SupplierValidationRecordFieldFilters => {
    return {
      assignee: undefined,
      status: undefined,
      organizationId: undefined,
      registrationDate: undefined,
      didUserRegister: false,
      automailerStatus: undefined,
      followUpEmailStatus: undefined,
      evidenceType: undefined,
      customerInvolvement: undefined,
      instructionType: undefined,
      followUpFilter: undefined,
      registrationAccountBankCountryCodes: undefined,
    };
  };

  createManualFollowUpRecordsFilter = (): SupplierValidationRecordFieldFilters => {
    return {
      assignee: undefined,
      status: [
        SupplierValidationRecordStatus.inProgress,
        SupplierValidationRecordStatus.topUpRequired,
        SupplierValidationRecordStatus.clarification,
        SupplierValidationRecordStatus.clarificationExternal,
        SupplierValidationRecordStatus.updateCompleted,
        SupplierValidationRecordStatus.completedWithNoEmails,
      ],
      organizationId: undefined,
      registrationDate: undefined,
      didUserRegister: undefined,
      automailerStatus: undefined,
      followUpEmailStatus: undefined,
      evidenceType: undefined,
      customerInvolvement: YesNoFilterOption.no,
      instructionType: undefined,
      followUpFilter: true,
      registrationAccountBankCountryCodes: undefined,
    };
  };

  private usersWithMeAtStart = (users: Assignee[]): Assignee[] => {
    const meAsAssignee = users?.find((user) => user.isMe);
    const sortedUsersListWithInactiveUsersLast = users?.sort(
      compare.byField((user: Assignee) => user.isAssignable, compare.booleans()).then(compare.byStringField((user) => user.name)),
    );
    return getListWithLeading(sortedUsersListWithInactiveUsersLast, meAsAssignee);
  };
}
